import { create, useStore } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import * as T from '^/types';
import { FinishProps } from '^/store/Utils';
import { deleteCDNCookie, setCDNCookie } from '^/utilities/cookie';
import { createStore } from 'zustand/vanilla';
import { SignUpSteps } from '^/components/organisms/SignUpForm/signupFormUtils';

export const useAuthStore = create<T.AuthState>()(
  devtools(
    persist(
      () => ({
        automaticSignIn: false,
        signInStatus: T.APIStatus.IDLE,
        signUpStatus: T.APIStatus.IDLE,
      }),
      { name: 'auth' }
    )
  )
);

// These functions below are for unhookable areas
// But we can use them also in functional components
export function startSignIn() {
  useAuthStore.setState({
    signInStatus: T.APIStatus.PROGRESS,
    signInError: undefined,
    signInErrorCode: undefined,
  });
}

export function cancelSignIn() {
  useAuthStore.setState({
    signInStatus: T.APIStatus.IDLE,
    signInError: undefined,
    signInErrorCode: undefined,
  });
}

export function resetSignInAPI() {
  useAuthStore.setState({
    signInStatus: T.APIStatus.IDLE,
    signInError: undefined,
    signInErrorCode: undefined,
  });
}

export function finishSignIn(
  state: FinishProps & {
    readonly errorCode?: number;
  }
) {
  useAuthStore.setState({
    signInStatus: state.error === undefined ? T.APIStatus.SUCCESS : T.APIStatus.ERROR,
    signInError: state.error,
    signInErrorCode: state.errorCode,
  });
}

export function startSignUp() {
  useAuthStore.setState({
    signUpStatus: T.APIStatus.PROGRESS,
    signUpError: undefined,
  });
}

export function cancelSignUp() {
  useAuthStore.setState({
    signUpStatus: T.APIStatus.IDLE,
    signUpError: undefined,
  });
}

export function resetSignUpAPI() {
  useAuthStore.setState({
    signUpStatus: T.APIStatus.IDLE,
    signUpError: undefined,
  });
}

export function finishSignUp(state: FinishProps) {
  useAuthStore.setState({
    signUpStatus: state.error === undefined ? T.APIStatus.IDLE : T.APIStatus.ERROR,
    signUpError: state.error,
  });
}

/**
 * @TODO
 * All these functions below are too complicated to apply
 * So these are to be seen later (when applying to other parts)
 */
export function changeAuthedUser(state: { readonly authedUser?: T.AuthState['authedUser'] }) {
  if (state.authedUser) {
    setCDNCookie(state.authedUser);
  } else {
    deleteCDNCookie();
  }

  useAuthStore.setState({
    authedUser: state.authedUser,
  });
}

export function changeAuthedUserAfterSignUp() {
  const { tempUser } = useAuthStore.getState();
  useAuthStore.setState({
    authedUser: tempUser
      ? {
          ...tempUser,
          cloudFrontPolicy: '',
          cloudFrontSignature: '',
          cloudFrontKeyPairId: '',
        }
      : undefined,
    tempUser: undefined,
  });
}

export function changeTempUser(state: { readonly tempUser?: T.AuthState['tempUser'] }) {
  useAuthStore.setState({
    tempUser: state.tempUser,
  });
}

export function changeAutomaticSignIn(state: {
  readonly automaticSignIn: T.AuthState['automaticSignIn'];
}) {
  useAuthStore.setState({
    automaticSignIn: state.automaticSignIn,
  });
}

export function changePathBeforeAuth(state: { readonly path: T.AuthState['pathBeforeAuth'] }) {
  useAuthStore.setState({
    ...state,
    pathBeforeAuth: state.path,
  });
}
interface SignUpStepsState {
  currentStep: string;
  completedSteps: string[];
}

interface SignUpStepsActions {
  setCurrentStep(currentStep: string): void;
  setCompletedSteps(completedStep: string): void;
  resetSteps(): void;
}

const initialState: SignUpStepsState = {
  currentStep: SignUpSteps.STEP1,
  completedSteps: [],
};

type SignUpStepsStore = SignUpStepsState & SignUpStepsActions;

export const signUpStepsStore = createStore<SignUpStepsStore>()(
  devtools(set => ({
    ...initialState,
    setCurrentStep(currentStep: string) {
      set({ currentStep });
    },
    setCompletedSteps(completedStep: string) {
      set(state => ({
        completedSteps: [...state.completedSteps, completedStep],
      }));
    },
    resetSteps() {
      set({ ...initialState });
    },
  }))
);

export function useStepStore(): SignUpStepsStore;
export function useStepStore<T>(selector: (state: SignUpStepsStore) => T): T;
export function useStepStore<T>(selector?: (state: SignUpStepsStore) => T) {
  return useStore(signUpStepsStore, selector!);
}
